/**
 * Champion → 3D model registry (#237). The per-champion "has 3D model" switch:
 * a champion listed here renders through the runtime 3D path
 * (`threeLayer.ts`); every other champion stays on the existing 2D
 * procedural/sprite renderer.
 *
 * SCOPE (this issue): the rogue only. The registry maps ONE champion id to the
 * committed rogue GLB. It is the single swap point — pointing a champion at a
 * different GLB is a data edit here, not a code change.
 *
 * #240: the Rogue champion now ships in content (`chm_EQeduxkZ7r`, slug
 * "rogue") — the mapping moved off the warrior stand-in (#238) onto the real
 * rogue champion id. The warrior reverts to the 2D renderer; selecting any
 * other champion keeps today's 2D rendering.
 *
 * #818: the Fighter (`chm_BJ4RLXqiBG`, slug "fighter") joins the 3D path with the
 * first asset off the generated-art pipeline — a Meshy base body with the armor
 * set conformed onto it and the runtime's `idle`/`walk` clips baked in. Champions
 * without an entry here still render in 2D.
 *
 * The Mage (`chm_0TAZFt86RV`, slug "mage") joins next, and is the first model to
 * ship a `cast` clip alongside `idle`/`walk` — built by
 * `tools/armor-rehearsal/build-mage.mjs` from Meshy's one-clip-per-file exports.
 *
 * PRESENTATION ONLY — champion ids are matched as opaque strings; nothing here
 * touches the sim or any hash.
 */

/** A champion's 3D model binding. */
export interface Champion3DModel {
  /** Opaque champion id (mirrors the content bundle / catalog ids). */
  readonly championId: string;
  /** Art slug for the committed GLB (folder/file stem). */
  readonly modelSlug: string;
  /** Path to the GLB under the client's static asset root (`public/`). */
  readonly assetPath: string;
}

/** The rogue model, attached to the real Rogue champion (#240). */
const ROGUE_MODEL: Champion3DModel = {
  // #576: deliberately literal — this registry IS the single swap point
  // (see file header); it is presentation-only and never touches the sim.
  // eslint-disable-next-line pit/no-hardcoded-champion-id
  championId: "chm_EQeduxkZ7r", // slug: rogue
  modelSlug: "rogue",
  assetPath: "models/rogue.glb",
};

/** The mage model — the first asset shipping a `cast` clip. */
const MAGE_MODEL: Champion3DModel = {
  // #576: deliberately literal — this registry IS the single swap point
  // (see file header); it is presentation-only and never touches the sim.
  // eslint-disable-next-line pit/no-hardcoded-champion-id
  championId: "chm_0TAZFt86RV", // slug: mage
  modelSlug: "mage",
  assetPath: "models/mage.glb",
};

/** The priest model. */
const PRIEST_MODEL: Champion3DModel = {
  // #576: deliberately literal — this registry IS the single swap point
  // (see file header); it is presentation-only and never touches the sim.
  // eslint-disable-next-line pit/no-hardcoded-champion-id
  championId: "chm_OoXhilEgJi", // slug: priest
  modelSlug: "priest",
  assetPath: "models/priest.glb",
};

/** The afflictor model. */
const AFFLICTOR_MODEL: Champion3DModel = {
  // #576: deliberately literal — this registry IS the single swap point
  // (see file header); it is presentation-only and never touches the sim.
  // eslint-disable-next-line pit/no-hardcoded-champion-id
  championId: "chm_GPNhe2YP3C", // slug: afflictor
  modelSlug: "afflictor",
  assetPath: "models/afflictor.glb",
};

/** The fighter model (#818's stand-in art replaced by the Meshy rig, #830). */
const FIGHTER_MODEL: Champion3DModel = {
  // #576: deliberately literal — this registry IS the single swap point
  // (see file header); it is presentation-only and never touches the sim.
  // eslint-disable-next-line pit/no-hardcoded-champion-id
  championId: "chm_BJ4RLXqiBG", // slug: fighter
  modelSlug: "fighter",
  assetPath: "models/fighter.glb",
};

/** Champion id → 3D model binding. */
const MODELS_BY_CHAMPION: ReadonlyMap<string, Champion3DModel> = new Map([
  [ROGUE_MODEL.championId, ROGUE_MODEL],
  [MAGE_MODEL.championId, MAGE_MODEL],
  [PRIEST_MODEL.championId, PRIEST_MODEL],
  [AFFLICTOR_MODEL.championId, AFFLICTOR_MODEL],
  [FIGHTER_MODEL.championId, FIGHTER_MODEL],
]);

/** Champion ids that opt into the 3D renderer (candidates for a GLB). */
export const CHAMPION_IDS_WITH_3D_MODEL: readonly string[] = [...MODELS_BY_CHAMPION.keys()];

/** True if `championId` renders through the 3D path. `null`/unknown → false. */
export function has3DModel(championId: string | null): boolean {
  return championId !== null && MODELS_BY_CHAMPION.has(championId);
}

/** The 3D model binding for a champion, or `null` if it stays 2D. */
export function model3DFor(championId: string | null): Champion3DModel | null {
  if (championId === null) return null;
  return MODELS_BY_CHAMPION.get(championId) ?? null;
}

/**
 * Resolve a model's runtime URL, honouring the Vite base path so it loads under
 * a sub-path deploy. Kept separate from the pure registry so the registry stays
 * trivially testable.
 */
export function model3DUrl(model: Champion3DModel): string {
  return `${import.meta.env.BASE_URL}${model.assetPath}`;
}
